// ChatPromptsAdmin.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ----------------------------------------------------------------
 * ChatPromptsAdmin
 *
 * Purpose:
 *   Admin UI to maintain tenant-scoped system prompts used by Course Chat,
 *   with separate prompts for Instructor and Student.
 *
 * Backend:
 *   - GET    {backEndUrl}/chat-prompts?role=instructor|student
 *            → { role, prompt, found, updatedAt? }
 *   - PUT    {backEndUrl}/chat-prompts
 *            body: { role, promptText } (or { role, prompt })
 *
 * Learn (role gate):
 *   - GET /learn/api/public/v1/users/me   (for institutionRoleIds)
 * ----------------------------------------------------------------*/

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        gate: {
            checking: "Checking permissions…",
            blockedTitle: "Access Restricted",
            blockedMsg: "The Chat Prompt Admin Tool is not available for your role.",
        },
        headings: {
            main: "Chat Prompts Admin",
            editor: "System Prompt Editor",
            preview: "Preview",
            error: "Error",
        },
        buttons: {
            refresh: "Refresh",
            edit: "Edit",
            cancel: "Cancel",
            save: "Save",
            saved: "Saved",
            saving: "Saving…",
            useDefault: "Reset to Default",
            copy: "Copy",
        },
        roles: {
            label: "Audience",
            instructor: "Instructor",
            student: "Student",
        },
        form: {
            promptLabel: "System prompt",
            promptHelp:
                "This prompt is stored per-tenant and per audience (Instructor/Student) and used by the Course Chat runtime. Keep it concise, clear, and policy-safe. Changes apply immediately for this tenant and audience.",
            placeholder: "Enter the system prompt that should guide the assistant…",
            charCount: "Characters",
        },
        notices: {
            loading: "Loading ChatPromptsAdmin…",
            loaded: "Prompt loaded.",
            saved: "Prompt saved.",
            failedLoad: "Failed to load prompt.",
            failedSave: "Failed to save prompt.",
            copied: "Copied to clipboard.",
            reset: "Reset to default prompt (not saved yet).",
        },
        confirm: {
            reset: "Reset the editor to the default prompt? This does not save until you click Save.",
        },
        validation: {
            missingPrompt: "Prompt text is required.",
            missingRole: "Role is required.",
            tooShort: "Prompt seems too short. Please provide more guidance.",
            tooLong: "Prompt is very long. Consider shortening for best results.",
        },
        // IMPORTANT: set this to whatever institution role you will use for gating
        roleId: "CHAT_PROMPTS_ADMIN",
    },
    en_AU: {},
};

const getMessages = (loc) => ({ ...MESSAGES.en_US, ...(MESSAGES[loc] || {}) });

/** ---------- Learn API adapter (role gate only) ---------- */
function makeLearnApi(platformUrl, token) {
    const base = (platformUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });

    const v1 = "/learn/api/public/v1";

    async function getCurrentUserInstitutionRoles() {
        const { data } = await http.get(`${v1}/users/me`);
        return data?.institutionRoleIds || [];
    }

    return { getCurrentUserInstitutionRoles };
}

/** ---------- Backend API adapter (tenant-aware) ---------- */
function makeBackendApi(backEndUrl, tenantId) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const baseHeaders = { Accept: "application/json" };
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { ...baseHeaders, ...(tenantId ? { "x-tenant-id": tenantId } : {}) },
    });

    return {
        getPrompt: async (role) => {
            const r = String(role || "").trim().toLowerCase();
            const qs = r ? `?role=${encodeURIComponent(r)}` : "";
            const { data } = await http.get(`/chat-prompts${qs}`);
            return data || {};
        },
        savePrompt: async (role, promptText) => {
            const { data } = await http.put(`/chat-prompts`, { role, promptText });
            return data || {};
        },
    };
}

/** ---------- Utilities ---------- */
function normalizePromptResponse(data, fallbackRole = "") {
    const role = String(
        data?.role ?? data?.Role ?? fallbackRole ?? ""
    )
        .trim()
        .toLowerCase();

    const prompt =
        data?.prompt ??
        data?.Prompt ??
        data?.promptText ??
        data?.PromptText ??
        "";

    const found = Boolean(data?.found ?? data?.Found ?? (prompt && String(prompt).trim().length > 0));
    const updatedAt = data?.updatedAt ?? data?.UpdatedAt ?? null;

    return {
        role,
        prompt: String(prompt || ""),
        found,
        updatedAt: updatedAt ? String(updatedAt) : "",
    };
}

function safeTrim(s) {
    return String(s ?? "").replace(/\r\n/g, "\n").trim();
}

function formatPreview(text) {
    const t = String(text || "");
    return t.split(/\n+/).map((p, i) => (
        <p key={i} style={{ margin: 0, whiteSpace: "pre-wrap" }}>
            {p}
        </p>
    ));
}

/** ---------- Defaults (role-specific) ---------- */
const DEFAULT_PROMPT_INSTRUCTOR = [
    "You are an expert Instructional Designer and faculty support assistant.",
    "When instructors ask about the course, answer using ONLY the provided course context for course structure, content, grading, and policies.",
    "If the context does not contain the answer, say you don't know and suggest where in the course they might check.",
    "You may use general pedagogy best practices when instructors ask for teaching strategies or accessibility guidance.",
    "Be concise, actionable, and provide step-by-step guidance when appropriate.",
    "Include short citations like [Title] that link to items when available.",
].join(" ");

const DEFAULT_PROMPT_STUDENT = [
    "You are a helpful course assistant supporting students with clear, step-by-step help.",
    "Answer using ONLY the provided course context when the question is about course structure, materials, assignments, due dates, grading, or policies.",
    "If the context does not contain the answer, say you don't know and suggest where the student can find it in the course.",
    "You may use general study skills and learning strategies when the student asks for help learning or planning.",
    "Be concise, encouraging, and accessibility-aware (including ADHD-friendly formatting).",
    "Include short citations like [Title] that link to items when available.",
].join(" ");

function defaultPromptForRole(role) {
    const r = String(role || "").toLowerCase();
    if (r === "student") return DEFAULT_PROMPT_STUDENT;
    return DEFAULT_PROMPT_INSTRUCTOR;
}

/** ---------- Component ---------- */
const ChatPromptsAdmin = ({ platformUrl, backEndUrl, locale = "en_US", tenantId }) => {
    const M = useMemo(() => getMessages(locale), [locale]);

    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const token = queryParams.get("token") || "";

    const learn = useMemo(() => makeLearnApi(platformUrl, token), [platformUrl, token]);
    const backend = useMemo(() => makeBackendApi(backEndUrl, tenantId), [backEndUrl, tenantId]);

    const ROLES = useMemo(
        () => [
            { id: "instructor", label: M.roles.instructor },
            { id: "student", label: M.roles.student },
        ],
        [M.roles.instructor, M.roles.student]
    );

    // Gate
    const [roleChecked, setRoleChecked] = useState(false);
    const [hasAdminRole, setHasAdminRole] = useState(false);

    // Data
    const [loading, setLoading] = useState(true);
    const [fatalError, setFatalError] = useState("");

    const [inlineError, setInlineError] = useState("");
    const [inlineSuccess, setInlineSuccess] = useState("");

    // Which audience are we editing?
    const [selectedRole, setSelectedRole] = useState("instructor");

    // Per-role state (keeps drafts when switching roles)
    const [byRole, setByRole] = useState(() => ({
        instructor: { loaded: false, serverPrompt: "", found: false, updatedAt: "", current: "", dirty: false },
        student: { loaded: false, serverPrompt: "", found: false, updatedAt: "", current: "", dirty: false },
    }));

    const [saving, setSaving] = useState(false);

    const compactCss = `
    .bb-container { font-size: 13px; }
    .bb-card h2 { font-size: 16px; font-weight: 700 !important; margin: 0; }
    .bb-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }

    .bb-card .bb-button,
    .bb-container .bb-button {
      -webkit-appearance: none !important;
      appearance: none !important;
      background: #f6f6f6 !important;
      border: 1px solid #d3d3d3 !important;
      color: #222 !important;
      border-radius: 6px !important;
      padding: 6px 10px !important;
      font: inherit !important;
      line-height: 1.2 !important;
      cursor: pointer !important;
    }
    .bb-card .bb-button:hover,
    .bb-container .bb-button:hover { background: #efefef !important; }
    .bb-card .bb-button:focus-visible { outline: 2px solid #5a9cff !important; outline-offset: 2px !important; }

    .bb-card .bb-button.primary { background: #2c6bed !important; border-color: #2c6bed !important; color: #fff !important; }
    .bb-card .bb-button.primary:hover { background: #255dd1 !important; border-color: #255dd1 !important; }

    .bb-card .bb-button.secondary { background: #f2f2f2 !important; border-color: #d0d0d0 !important; color: #222 !important; }
    .bb-card .bb-button.toggle.active { background: #2c6bed !important; border-color: #2c6bed !important; color: #fff !important; }

    textarea, input { font-size: 12px; padding: 8px 10px; font-weight: 400; color: inherit; border-radius: 6px; border: 1px solid #ddd; }
  `;

    const Flash = ({ type, children }) => {
        if (!children) return null;
        const styles = {
            marginBottom: 6,
            padding: "8px 10px",
            borderRadius: 6,
            border: "1px solid",
            background: type === "error" ? "#ffecec" : "#ecfff1",
            borderColor: type === "error" ? "#ffb3b3" : "#b3ffcc",
            color: type === "error" ? "#a40000" : "#005c2e",
            fontSize: 12,
            whiteSpace: "pre-wrap",
        };
        return (
            <div role={type === "error" ? "alert" : "status"} style={styles} aria-live="polite">
                {children}
            </div>
        );
    };

    const currentRoleState = byRole[selectedRole] || byRole.instructor;
    const charCount = String(currentRoleState.current || "").length;
    const isLikelyTooLong = charCount > 5000;

    /** ---------------- Role gate ---------------- */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                if (!platformUrl || !token) {
                    if (!cancelled) {
                        setHasAdminRole(false);
                        setRoleChecked(true);
                    }
                    return;
                }
                const roles = await learn.getCurrentUserInstitutionRoles();
                if (!cancelled) {
                    setHasAdminRole(Array.isArray(roles) && roles.includes(M.roleId));
                    setRoleChecked(true);
                }
            } catch {
                if (!cancelled) {
                    setHasAdminRole(false);
                    setRoleChecked(true);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [learn, platformUrl, token, M.roleId]);

    /** ---------------- Load prompt (per role) ---------------- */
    async function loadPromptForRole(role, { clearMessages = false, suppressSpinner = false } = {}) {
        const r = String(role || "").trim().toLowerCase();
        if (!r) return;

        if (!suppressSpinner) setLoading(true);
        setFatalError("");
        if (clearMessages) {
            setInlineError("");
            setInlineSuccess("");
        }

        try {
            const data = await backend.getPrompt(r);
            const norm = normalizePromptResponse(data, r);

            setByRole((prev) => {
                const prevRole = prev[norm.role] || prev[r] || { loaded: false, serverPrompt: "", found: false, updatedAt: "", current: "", dirty: false };
                const dirty = Boolean(prevRole.dirty);

                return {
                    ...prev,
                    [r]: {
                        ...prevRole,
                        loaded: true,
                        serverPrompt: norm.prompt,
                        found: norm.found,
                        updatedAt: norm.updatedAt,
                        // Only refresh the editor if not dirty (keep draft)
                        current: dirty ? prevRole.current : norm.prompt || "",
                    },
                };
            });

            if (clearMessages) setInlineSuccess(M.notices.loaded);
        } catch (err) {
            setFatalError(err?.response?.data?.message || err.message || M.notices.failedLoad);
        } finally {
            if (!suppressSpinner) setLoading(false);
        }
    }

    // Initial load: prefetch both roles (fast switching)
    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!(roleChecked && hasAdminRole)) return;
            try {
                setLoading(true);
                setFatalError("");
                setInlineError("");
                setInlineSuccess("");

                await Promise.all(
                    ROLES.map(async (r) => {
                        if (cancelled) return;
                        return loadPromptForRole(r.id, { clearMessages: false, suppressSpinner: true });
                    })
                );

                if (!cancelled) {
                    setInlineSuccess(M.notices.loaded);
                }
            } catch (err) {
                if (!cancelled) {
                    setFatalError(err?.response?.data?.message || err.message || M.notices.failedLoad);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleChecked, hasAdminRole, backEndUrl, tenantId]);

    // If user changes selectedRole and it wasn't loaded (edge), load it
    useEffect(() => {
        if (!(roleChecked && hasAdminRole)) return;
        if (!byRole[selectedRole]?.loaded) {
            loadPromptForRole(selectedRole, { clearMessages: false, suppressSpinner: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRole]);

    /** ---------------- Editor helpers ---------------- */
    function setRoleCurrent(role, patch) {
        const r = String(role || "").toLowerCase();
        setByRole((prev) => ({ ...prev, [r]: { ...(prev[r] || {}), ...patch } }));
    }

    function beginEdit() {
        setInlineError("");
        setInlineSuccess("");
        setRoleCurrent(selectedRole, {
            current: currentRoleState.serverPrompt || "",
            dirty: false,
        });
    }

    function cancelEdit() {
        setInlineError("");
        setInlineSuccess("");
        setRoleCurrent(selectedRole, {
            current: currentRoleState.serverPrompt || "",
            dirty: false,
        });
    }

    function resetToDefault() {
        const okConfirm = window.confirm(M.confirm.reset);
        if (!okConfirm) return;
        setInlineError("");
        setInlineSuccess(M.notices.reset);
        setRoleCurrent(selectedRole, {
            current: defaultPromptForRole(selectedRole),
            dirty: true,
        });
    }

    function updateText(v) {
        setRoleCurrent(selectedRole, { current: v, dirty: true });
    }

    /** ---------------- Save ---------------- */
    async function saveCurrent() {
        setInlineError("");
        setInlineSuccess("");

        const role = String(selectedRole || "").trim().toLowerCase();
        if (!role) {
            setInlineError(M.validation.missingRole);
            return;
        }

        const promptText = safeTrim(currentRoleState.current);
        if (!promptText) {
            setInlineError(M.validation.missingPrompt);
            return;
        }

        if (promptText.length < 60) {
            setInlineError(M.validation.tooShort);
            return;
        }

        setSaving(true);
        try {
            await backend.savePrompt(role, promptText);
            setInlineSuccess(M.notices.saved);

            // mark clean, then refresh from server
            setRoleCurrent(role, { dirty: false });
            await loadPromptForRole(role, { suppressSpinner: true });
        } catch (err) {
            setInlineError(err?.response?.data?.message || err.message || M.notices.failedSave);
        } finally {
            setSaving(false);
        }
    }

    async function copyToClipboard() {
        setInlineError("");
        setInlineSuccess("");
        try {
            await navigator.clipboard.writeText(String(currentRoleState.current || ""));
            setInlineSuccess(M.notices.copied);
        } catch {
            setInlineError("Could not copy to clipboard.");
        }
    }

    /** ---------------- Gates ---------------- */
    if (!roleChecked) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.gate.checking}</div>
            </div>
        );
    }

    if (!hasAdminRole) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2>{M.gate.blockedTitle}</h2>
                    <p style={{ fontSize: 12 }}>{M.gate.blockedMsg}</p>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div>{M.notices.loading}</div>
            </div>
        );
    }

    if (fatalError) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>{M.headings.error}</h2>
                    <p style={{ fontSize: 12 }}>{fatalError}</p>
                </div>
            </div>
        );
    }

    /** ---------------- Main UI ---------------- */
    return (
        <div className="bb-container" style={{ display: "grid", gap: 12 }}>
            <style dangerouslySetInnerHTML={{ __html: compactCss }} />

            {/* Header */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between" }}>
                    <h2>{M.headings.main}</h2>
                    <div className="bb-row">
                        <button
                            type="button"
                            className="bb-button secondary"
                            onClick={() => loadPromptForRole(selectedRole, { clearMessages: true, suppressSpinner: false })}
                            disabled={saving}
                        >
                            {M.buttons.refresh}
                        </button>
                    </div>
                </div>

                <div className="bb-row" style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginRight: 6 }}>{M.roles.label}:</div>
                    <div className="bb-row" style={{ gap: 6 }}>
                        {ROLES.map((r) => (
                            <button
                                key={r.id}
                                type="button"
                                className={`bb-button toggle ${selectedRole === r.id ? "active" : ""}`}
                                onClick={() => {
                                    setInlineError("");
                                    setInlineSuccess("");
                                    setSelectedRole(r.id);
                                }}
                                disabled={saving}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ fontSize: 12, color: "#555", marginTop: 8 }}>
                    <div>
                        Tenant: <b>{tenantId || "(none)"}</b>
                        {" "}• Role: <b>{selectedRole}</b>
                        {" "}• Stored: <b>{currentRoleState.found ? "Yes" : "No (runtime may use default if configured)"}</b>
                        {currentRoleState.updatedAt ? (
                            <>
                                {" "}• Updated: <b>{String(currentRoleState.updatedAt).slice(0, 19)}</b>
                            </>
                        ) : null}
                    </div>
                    {currentRoleState.dirty ? (
                        <div style={{ marginTop: 4 }}>
                            Status: <b>Unsaved changes</b>
                        </div>
                    ) : null}
                </div>
            </div>

            {/* Editor */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
                    <h2>{M.headings.editor}</h2>

                    <div className="bb-row">
                        <button type="button" className="bb-button" onClick={beginEdit} disabled={saving}>
                            {M.buttons.edit}
                        </button>

                        <button type="button" className="bb-button secondary" onClick={resetToDefault} disabled={saving}>
                            {M.buttons.useDefault}
                        </button>

                        <button type="button" className="bb-button secondary" onClick={copyToClipboard} disabled={saving}>
                            {M.buttons.copy}
                        </button>

                        <button
                            type="button"
                            className="bb-button primary"
                            onClick={saveCurrent}
                            disabled={!currentRoleState.dirty || saving}
                            aria-disabled={!currentRoleState.dirty || saving}
                            title={M.buttons.save}
                        >
                            {saving ? M.buttons.saving : currentRoleState.dirty ? M.buttons.save : M.buttons.saved}
                        </button>

                        <button type="button" className="bb-button secondary" onClick={cancelEdit} disabled={saving}>
                            {M.buttons.cancel}
                        </button>
                    </div>
                </div>

                <Flash type="error">{inlineError}</Flash>
                <Flash type="success">{inlineSuccess}</Flash>

                {isLikelyTooLong ? <Flash type="error">{M.validation.tooLong}</Flash> : null}

                <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                    {M.form.promptLabel} ({selectedRole})
                </label>

                <textarea
                    value={currentRoleState.current}
                    onChange={(e) => updateText(e.target.value)}
                    placeholder={M.form.placeholder}
                    rows={14}
                    style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }}
                    disabled={saving}
                />

                <div className="bb-row" style={{ justifyContent: "space-between", marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: "#555" }}>{M.form.promptHelp}</div>
                    <div style={{ fontSize: 11, color: "#555" }}>
                        {M.form.charCount}: <b>{charCount}</b>
                    </div>
                </div>
            </div>

            {/* Preview */}
            <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                <div className="bb-row" style={{ justifyContent: "space-between", marginBottom: 6 }}>
                    <h2>{M.headings.preview}</h2>
                    <div style={{ fontSize: 12, color: "#555" }}>Preview is for readability only (does not call the backend).</div>
                </div>

                <div style={{ border: "1px solid #eee", borderRadius: 6, padding: 10, background: "#fafafa", fontSize: 12 }}>
                    {formatPreview(currentRoleState.current || "")}
                </div>
            </div>
        </div>
    );
};

export default ChatPromptsAdmin;